import {
  Job,
  StateMachineState,
  JobOptions,
  JobQueueJobs,
} from './types'
import { Tab, TabEventMap } from 'tab-election'
import { log } from '../logger'
import { JobQueueDatabase } from './JobQueueDatabase'
import { isJobFinished } from './helpers'
import { delay } from '@pogokid/util'

export interface JobQueueClientOptions {
  namespace?: string
  init?: () => Promise<void>
}

export type InferJobQueueClientState<QC> =
  QC extends JobQueueClient<infer S, JobQueueJobs> ? S : never

export type InferJobQueueClientJobs<QC> =
  QC extends JobQueueClient<StateMachineState, infer J>
    ? J
    : never

export class JobQueueClient<
  State extends StateMachineState,
  Jobs extends JobQueueJobs
> {
  private tab: Tab
  private database: JobQueueDatabase

  constructor(options: JobQueueClientOptions = {}) {
    this.tab = new Tab(options.namespace || 'web-queue')
    this.database = new JobQueueDatabase()
    this.database.init().catch((error: Error) => {
      log.error(
        'web-queue:database error initialising db',
        error
      )
    })
  }

  addEventListener<K extends keyof TabEventMap>(
    type: K,
    listener: (
      this: BroadcastChannel,
      ev: TabEventMap[K]
    ) => unknown,
    options?: boolean | AddEventListenerOptions
  ): void
  addEventListener(type: string, listener: EventListener) {
    return this.tab.addEventListener(type, listener)
  }

  removeEventListener<K extends keyof TabEventMap>(
    type: K,
    listener: (
      this: BroadcastChannel,
      ev: TabEventMap[K]
    ) => unknown,
    options?: boolean | EventListenerOptions
  ): void
  removeEventListener(type: string, listener: EventListener) {
    return this.tab.removeEventListener(type, listener)
  }

  private callVoid(name: string, ...rest: unknown[]) {
    this.tab.call(name, ...rest).catch((error) => {
      log.error('web-queue:callError', error)
    })
  }

  startProcessing(initialState?: Partial<State>): void {
    if (initialState) {
      this.setState(initialState).then(() => {
        this.callVoid('startProcessing')
      })
    } else {
      this.callVoid('startProcessing')
    }
  }

  async enqueue<K extends keyof Jobs, D = Jobs[K]>(
    type: K,
    data?: D,
    options: JobOptions = {}
  ): Promise<string> {
    return this.tab.call('enqueue', type, data, options)
  }

  enqueueSync<K extends keyof Jobs>(
    type: K,
    data: Jobs[K],
    options: JobOptions = {}
  ): void {
    this.enqueue(type, data, options)
      .then((jobId) => {
        log.debug('web-queue:enqueue', 'queued job', jobId)
      })
      .catch((error) => {
        log.error('web-queue:enqueue', error)
      })
  }

  setState(state: Partial<State>) {
    log.debug('web-queue:setState', Object.keys(state))
    return this.tab.call('updateState', state)
  }

  getState(): State {
    return this.tab.getState() as State
  }

  async cancelJob(jobId: string): Promise<boolean> {
    return this.tab.call('cancelJob', jobId)
  }

  async destroy(): Promise<void> {
    // Spoke cleanup would be handled by tab-election
  }

  async clear(): Promise<void> {
    // await this.tab.call('stopProcessing')
    await this.database.clear()
  }

  getJob(jobId: string): Promise<Job | undefined> {
    return this.database.getJob(jobId)
  }

  async waitForJob<R>(jobId: string) {
    let job: Job<unknown, R> | undefined | null = null
    const timeout = Date.now() + 6_000
    while (!job) {
      if (Date.now() > timeout) {
        throw new Error(`web-queue:waitForJob timeout ${jobId}`)
      }
      job = (await this.getJob(jobId)) as
        | Job<unknown, R>
        | undefined
      if (job && isJobFinished(job)) {
        return job
      } else {
        job = null
        await delay(100)
      }
    }
  }
}
